const POST_VERIFY_REDIRECT_KEY = "rakb_post_verify_redirect";

/**
 * Internal path only. Blocks open redirects and the post-signup KYC screens.
 */
export const isSafeInternalRedirect = (value: string | null | undefined): value is string => {
  if (!value) return false;
  const path = value.trim();
  if (!path.startsWith("/") || path.startsWith("//") || path.startsWith("/\\")) return false;
  if (path.includes("://") || path.includes("\\")) return false;

  const pathname = path.split("#")[0]?.split("?")[0] ?? "";
  if (
    pathname === "/auth" ||
    pathname.startsWith("/auth/") ||
    pathname === "/verify" ||
    pathname.startsWith("/verify/") ||
    pathname === "/documents/verification" ||
    pathname.startsWith("/documents/verification/")
  ) {
    return false;
  }

  return true;
};

export const resolvePostVerifyRedirect = (candidate: string | null | undefined): string =>
  isSafeInternalRedirect(candidate) ? candidate : "/search";

/** Same-origin car or search URL the user had open before signup. */
export const readCarPageFromReferrer = (
  referrer: string | null | undefined,
  origin: string
): string | null => {
  if (!referrer || !origin) return null;
  try {
    const ref = new URL(referrer);
    if (ref.origin !== origin) return null;
    const path = `${ref.pathname}${ref.search}`;
    const isCarOrSearch = path.startsWith("/cars/") || path === "/search" || path.startsWith("/search?");
    if (!isCarOrSearch) return null;
    return isSafeInternalRedirect(path) ? path : null;
  } catch {
    return null;
  }
};

/**
 * Supabase emailRedirectTo.
 * Shape: `{origin}/auth/confirm?next=/auth/email-verified?redirectTo=[url]`
 * `next` is encoded so the inner query survives.
 */
export const buildEmailConfirmRedirect = (baseUrl: string, redirectTo?: string | null): string => {
  const origin = baseUrl.replace(/\/$/, "");
  const safe = isSafeInternalRedirect(redirectTo) ? redirectTo : null;
  const next = safe
    ? `/auth/email-verified?redirectTo=${encodeURIComponent(safe)}`
    : "/auth/email-verified";
  return `${origin}/auth/confirm?next=${encodeURIComponent(next)}`;
};

/** Reads redirectTo nested in AuthConfirm's `next` param. */
export const readRedirectFromNext = (next: string | null | undefined): string => {
  if (!next) return "/search";
  try {
    const url = new URL(next, "https://rakb.local");
    const nested = url.searchParams.get("redirectTo");
    if (isSafeInternalRedirect(nested)) return nested;
    if (url.pathname !== "/auth/email-verified" && url.pathname !== "/auth/confirm") {
      const path = `${url.pathname}${url.search}`;
      if (isSafeInternalRedirect(path)) return path;
    }
  } catch {
    if (isSafeInternalRedirect(next)) return next;
  }
  return "/search";
};

export const resolveAuthConfirmDestination = (
  next: string | null | undefined,
  redirectTo: string | null | undefined
): string => {
  if (isSafeInternalRedirect(redirectTo)) return redirectTo;
  return readRedirectFromNext(next);
};

export const rememberPostVerifyRedirect = (redirectTo: string | null | undefined): void => {
  try {
    if (isSafeInternalRedirect(redirectTo)) {
      sessionStorage.setItem(POST_VERIFY_REDIRECT_KEY, redirectTo);
      return;
    }
    sessionStorage.removeItem(POST_VERIFY_REDIRECT_KEY);
  } catch {
    // sessionStorage unavailable
  }
};

export const recallPostVerifyRedirect = (): string | null => {
  try {
    const stored = sessionStorage.getItem(POST_VERIFY_REDIRECT_KEY);
    return isSafeInternalRedirect(stored) ? stored : null;
  } catch {
    return null;
  }
};
